import { type FC, useState } from "react";
import { Form } from "react-bootstrap";
import { TIKTOK_ADS_MIN_DAILY_BUDGET } from "shared";
import { useModal } from "../common/modal/Modal";
import { Button } from "../components/Button";
import { ModalConfirmation } from "../settings/ModalConfirmation";

interface StatusToggleProps {
	objectId: string;
	status: string;
	isPending: boolean;
	label?: string;
	onToggle: (objectId: string, status: "ENABLE" | "DISABLE") => void;
}

/**
 * Pausing or activating changes what the account spends, so the button asks
 * inline for confirmation rather than firing on the first click.
 */
export const TikTokStatusToggle: FC<StatusToggleProps> = ({
	objectId,
	status,
	isPending,
	label = "this",
	onToggle,
}) => {
	const [confirming, setConfirming] = useState(false);
	const isActive = status === "ENABLE";
	const nextStatus = isActive ? "DISABLE" : "ENABLE";

	if (confirming) {
		return (
			<span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
				<span style={{ fontSize: "0.8rem" }}>
					{isActive ? `Pause ${label}?` : `Activate ${label}?`}
				</span>
				<Button
					size="sm"
					disabled={isPending}
					onClick={() => {
						onToggle(objectId, nextStatus);
						setConfirming(false);
					}}
				>
					Yes
				</Button>
				<Button
					size="sm"
					variant="secondary"
					onClick={() => setConfirming(false)}
				>
					No
				</Button>
			</span>
		);
	}

	return (
		<Button
			size="sm"
			variant="secondary"
			disabled={isPending}
			onClick={() => setConfirming(true)}
		>
			{isActive ? "Pause" : "Activate"}
		</Button>
	);
};

interface BudgetEditorProps {
	objectId: string;
	currentBudget?: number;
	isPending: boolean;
	promptNoun?: string;
	onSave: (objectId: string, budget: number) => void | Promise<void>;
}

export const TikTokBudgetEditor: FC<BudgetEditorProps> = ({
	objectId,
	currentBudget,
	isPending,
	promptNoun = "campaign",
	onSave,
}) => {
	const { open } = useModal();
	const [editing, setEditing] = useState(false);
	const [value, setValue] = useState(String(currentBudget ?? ""));

	const handleSave = () => {
		const budget = Number(value);
		if (!Number.isFinite(budget) || budget < TIKTOK_ADS_MIN_DAILY_BUDGET) {
			return;
		}
		open(ModalConfirmation, {
			prompt: `Change this ${promptNoun}'s budget to ${budget}?`,
			confirmText: "Update budget",
			variant: "primary",
			onConfirm: async () => {
				await onSave(objectId, budget);
				setEditing(false);
			},
		});
	};

	if (!editing) {
		return (
			<span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
				<span>{currentBudget ?? "—"}</span>
				<Button
					size="sm"
					variant="secondary"
					onClick={() => setEditing(true)}
					disabled={isPending}
				>
					Edit
				</Button>
			</span>
		);
	}

	return (
		<span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
			<Form.Control
				type="number"
				size="sm"
				min={TIKTOK_ADS_MIN_DAILY_BUDGET}
				step="1"
				value={value}
				onChange={(e) => setValue(e.target.value)}
				style={{ width: 90 }}
			/>
			<Button size="sm" onClick={handleSave} disabled={isPending}>
				Save
			</Button>
			<Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
				Cancel
			</Button>
		</span>
	);
};
